import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ModelCard.css';

const createMarkerIcon = () => {
  return new L.Icon({
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

L.Marker.prototype.options.icon = createMarkerIcon();

const formatDate = (value) => {
  const date = value?.toDate?.() || value;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'Fecha no disponible';
  return parsed.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const ModelCard = ({ model }) => {
  const [showMap, setShowMap] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const hasLocation = useMemo(() => (
    model.locationCoords &&
    model.locationCoords.latitude &&
    model.locationCoords.longitude
  ), [model.locationCoords]);

  const position = useMemo(() => (
    hasLocation
      ? [model.locationCoords.latitude, model.locationCoords.longitude]
      : null
  ), [hasLocation, model.locationCoords]);

  const toggleMap = (e) => {
    e.preventDefault();
    if (!hasLocation) return;
    setShowMap(prev => !prev);
  };

  const openInExternalMaps = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasLocation) {
      window.open(`https://www.google.com/maps?q=${position[0]},${position[1]}`, '_blank');
    }
  };

  useEffect(() => {
    if (showMap && mapRef.current && position) {
      setTimeout(() => {
        mapRef.current.flyTo(position, 15);
      }, 100);
    }
  }, [showMap, position]);

  useEffect(() => {
    if (!mapRef.current) return;

    const handleResize = () => {
      mapRef.current.invalidateSize();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <article className="model-card-container">
      <div className="model-card">
        <div className="model-image-container">
          {model.thumbnailUrl ? (
            <img
              src={model.thumbnailUrl}
              alt={model.name}
              className="model-image"
              onError={(e) => {
                e.target.src = '/default-thumbnail.jpg';
                e.target.onerror = null;
              }}
            />
          ) : (
            <div className="image-placeholder">
              <svg className="placeholder-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <span className="model-badge">Modelo Público</span>
        </div>

        <div className="model-content">
          <div className="model-header">
            <h3 className="model-title">{model.name}</h3>
            <span className="model-date">{formatDate(model.createdAt)}</span>
          </div>

          <div className="model-meta">
            <div className="model-category">
              <svg className="meta-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              <span>{model.category || 'Sin categoría'}</span>
            </div>
            <div className="model-era">
              <svg className="meta-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{model.era || 'Sin época definida'}</span>
            </div>
          </div>

          <div className="model-description-container">
            <p className="model-description">
              {model.description || 'No hay descripción disponible'}
            </p>
          </div>

          <div className={`model-map-container ${showMap ? 'visible' : ''}`}>
            {hasLocation && (
              <>
                <div className="map-header">
                  <h4>Ubicación: {model.location || 'Ubicación del modelo'}</h4>
                  <button
                    className="close-map-btn"
                    onClick={toggleMap}
                    aria-label={showMap ? 'Ocultar mapa' : 'Mostrar mapa'}
                  >
                    <svg className="close-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showMap ? 'M6 18L18 6M6 6l12 12' : 'M19 9l-7 7-7-7'} />
                    </svg>
                  </button>
                </div>
                {showMap && (
                  <MapContainer
                    ref={mapRef}
                    center={position}
                    zoom={15}
                    scrollWheelZoom={false}
                    className="model-map"
                    whenCreated={(map) => {
                      mapRef.current = map;
                      setTimeout(() => map.invalidateSize(), 0);
                    }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Marker
                      position={position}
                      ref={markerRef}
                    >
                      <Popup>
                        <div className="map-popup-content">
                          <strong>{model.name}</strong>
                          <p>{model.location || 'Ubicación del modelo'}</p>
                          <button
                            onClick={openInExternalMaps}
                            className="open-external-btn"
                          >
                            Abrir en Google Maps
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                )}
              </>
            )}
          </div>

          <div className="model-footer">
            {model.location && (
              <div
                className={`model-location ${hasLocation ? 'clickable' : ''}`}
                onClick={hasLocation ? toggleMap : undefined}
                title={hasLocation ? (showMap ? 'Ocultar mapa' : 'Mostrar mapa') : 'No hay coordenadas disponibles'}
              >
                <svg className="location-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span
                  className={`location-text ${hasLocation ? 'clickable' : ''}`}
                  onClick={hasLocation ? openInExternalMaps : undefined}
                  title={hasLocation ? 'Abrir en Google Maps' : 'No hay coordenadas disponibles'}
                >
                  {model.location}
                </span>
              </div>
            )}

            <Link to={`/model/${model.id}`} className="view-details-btn">
              Ver detalles
              <svg className="details-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ModelCard;
